import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { PutRequest } from "../../Utils/hooks/ApiHandler";
import { toast } from "sonner";
import { getCountries, getStates, getCities } from "../../Utils/hooks/location";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen } from "@fortawesome/free-solid-svg-icons";
import { customStyles } from "../../Utils/helpers/commonhelper";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetEssentials } from "../../Utils/hooks/useGetEssentials";

const locationOptionSchema = z.object({
  label: z.string().min(1, "Please select an option"),
  value: z.string().min(1, "Please select an option"),
});
const addressSchema = z.object({
  address: z.string().min(1, "Address is required"),
  country: locationOptionSchema,
  state: locationOptionSchema,
  city: locationOptionSchema,
  zipcode: z.string().min(1, "Zipcode is required"),
})

export const PurchaseAddressModal = ({
  show,
  handleClose,
  billing_address,
  handleFetch,
  defaultFor,
}) => {
  // getting essentials for apis
  const { customer, setCustomer, headers, extras, navigate, customerId, loginUserId, userType } = useGetEssentials()

  const [showAddForm, setShowAddForm] = useState(false);

  // Dynamic states
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      address: "",
      country: { label: "", value: "" },
      state: { label: "", value: "" },
      city: { label: "", value: "" },
      zipcode: "",
    },
  });

  // Fetch countries on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await getCountries();
        if (!data) return;
        setCountries(
          data.map((country) => ({
            value: country.id,
            label: country.name,
          }))
        );
      } catch (error) {
        toast.error("Error fetching countries");
      }
    })();
  }, []);

  // Fetch states when country changes
  const countryId = watch("country");
  const stateId = watch("state");
  useEffect(() => {
    (async () => {
      // if (!countryId) {
      //   setStates([]);
      //   return;
      // }
      try {
        const data = await getStates(countryId?.value || "99");
        if (!data) return;
        setStates(
          data.map((state) => ({
            value: state.id,
            label: state.state_name,
          }))
        );
      } catch (error) {
        toast.error("Error fetching states");
      }
    })();
  }, [countryId]);

  // Fetch cities when state changes
  useEffect(() => {
    (async () => {
      if (!stateId?.value) {
        setCities([]);
        return;
      }
      try {
        const data = await getCities(stateId.value);
        if (!data) return;
        setCities(
          data.map((city) => ({
            value: city.id,
            label: city.city_name,
          }))
        );
      } catch (error) {
        toast.error("Error fetching cities");
      }
    })();
  }, [stateId]);

  // addresses state
  const [addresses, setAddresses] = useState([]);
  useEffect(() => {
    setAddresses(billing_address)
  }, [defaultFor, billing_address]);

  // FIXED Select handler
  const handleSelectAddress = async (addr, selectedIndex) => {
    let updatedAddresses = [];

    if (defaultFor === "billing") {
      updatedAddresses = addresses.map((item, i) => ({
        ...item, // Keep all existing properties
        selected: i === selectedIndex ? "selected" : "", // Clear others, set selected
        for: item.for || "", // Preserve any existing shipping selection
      }));
    }

    if (defaultFor === "shipping") {
      updatedAddresses = addresses.map((item, i) => ({
        ...item, // Keep all existing properties
        selected: item.selected || "", // Preserve any existing billing selection
        for: i === selectedIndex ? "shipping" : "", // Clear others, set shipping
      }));
    }

    const payload = {
      customer_id: customerId,
      billing_address: updatedAddresses,
    };

    try {
      const res = await PutRequest(
        `/purchase/createBillingAddress/`,
        payload,
        { headers },
        extras
      );

      if (res.status === "success") {
        toast.success(res.message);

        let updatedList = [];
        try {
          updatedList = JSON.parse(res.data.billing_address);
        } catch (err) {
          updatedList = updatedAddresses; // Fallback to local data
        }

        setAddresses(updatedList);
        handleFetch(); // Refresh parent data
        handleClose(true);
      } else {
        toast.error(res.message || "Failed to update address");
        return
      }
    } catch (error) {
      toast.error("Failed to update address");
    }
  };

  // new address submit handler
  const handleFormSubmit = async (data) => {
    const address_data = {
      ...data,
      country: data.country || { label: "", value: "" },
      state: data.state || { label: "", value: "" },
      city: data.city || { label: "", value: "" },
      selected: defaultFor === "billing" ? "selected" : "",
      for: defaultFor === "shipping" ? "shipping" : "",
    };

    let previous_address = [];
    if (defaultFor === "billing") {
      previous_address = billing_address.map((c) => ({ ...c, selected: "" }));
    } else {
      previous_address = billing_address.map((c) => ({ ...c, for: "" }));
    }

    const payload = {
      customer_id: customerId,
      billing_address: [...previous_address, address_data],
    };
    try {
      const res = await PutRequest("purchase/createBillingAddress/", payload, { headers }, extras);
      if (res.status === "fail" || res.success === false) {
        toast.error(res.message);
        return;
      }
      toast.success(res.message);
      reset({
        address: "",
        country: { label: "", value: "" },
        state: { label: "", value: "" },
        city: { label: "", value: "" },
        zipcode: "",
      });
      handleFetch();
      setShowAddForm(false);
    } catch (error) {
      toast.error("Failed to add address");
    }
  };

  // first fetching the address to be edited and then opening the address form
  const handleEditAddress = (addr, i) => {
    setEditIndex(i);
    setIsEditing(true);
    reset({
      address: addr.address || "",
      country:
        addr.country && typeof addr.country === "object"
          ? addr.country
          : { label: addr.country || "", value: addr.country_id || addr.country || "" },
      state:
        addr.state && typeof addr.state === "object"
          ? addr.state
          : { label: addr.state || "", value: addr.state_id || addr.state || "" },
      city:
        addr.city && typeof addr.city === "object"
          ? addr.city
          : { label: addr.city || "", value: addr.city_id || addr.city || "" },
      zipcode: addr.zipcode || "",
    });


    setShowAddForm(true);
  };

  // Edit save handler
  const handleEditSave = async (data) => {
    if (editIndex === null) return;

    const updatedAddresses = addresses.map((item, i) => {
      return i === editIndex
        ? {
          ...item, // Keep existing properties like selected/for
          address: data.address,
          country: data.country || { label: "", value: "" },
          state: data.state || { label: "", value: "" },
          city: data.city || { label: "", value: "" },
          zipcode: data.zipcode,
        }
        : item
    }
    );

    const payload = {
      customer_id: customerId,
      billing_address: updatedAddresses,
    };

    try {
      const res = await PutRequest(
        `/purchase/createBillingAddress/`,
        payload,
        { headers },
        extras
      );

      if (res.status === "success") {
        toast.success(res.message);

        let updatedList = [];
        try {
          updatedList = JSON.parse(res.data.billing_address);
        } catch (err) {
          updatedList = updatedAddresses;
        }

        setAddresses(updatedList);
        handleFetch();

        // Reset editing state
        setEditIndex(null);
        setIsEditing(false);
        setShowAddForm(false);
        reset({
          address: "",
          country: { label: "", value: "" },
          state: { label: "", value: "" },
          city: { label: "", value: "" },
          zipcode: "",
        });
      } else {
        toast.error(res.message || "Failed to update address");
      }
    } catch (error) {
      toast.error("Failed to update address");
    }
  };


  return (
    <Modal show={show} onHide={() => {
      reset({
        address: "",
        country: { label: "", value: "" },
        state: { label: "", value: "" },
        city: { label: "", value: "" },
        zipcode: "",
      });
      handleClose()
    }} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {defaultFor === "billing" ? "Billing Addresses" : "Shipping Addresses"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {!showAddForm ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Select Address</h6>
              <Button
                className="btn btn-default"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                + Add Address
              </Button>
            </div>
            {addresses?.length > 0 ? (
              addresses.map((addr, i) => (
                <div
                  key={i}
                  className="d-flex align-items-center justify-content-between mb-2 p-2 border rounded"
                  style={{ cursor: "pointer" }}
                >
                  <div
                    className="d-flex align-items-center flex-grow-1"
                    onClick={() => handleSelectAddress(addr, i)}
                  >
                    <Form.Check
                      type="radio"
                      name="selectedAddress"
                      checked={
                        defaultFor === "billing"
                          ? addr.selected === "selected"
                          : addr.for === "shipping"
                      }
                      readOnly
                      style={{ width: "24px", height: '24px' }}
                      className="me-3 mt-1 custom-radio"
                    />
                    <div>
                      <p className="mb-0 fs-13 fw-500">{addr.address}</p>
                      <p className="mb-0 fs-13 text-dark">
                        {addr.city.label}, {addr.state.label}, {addr.country.label} - {addr.zipcode}
                      </p>
                    </div>
                  </div>

                  <FontAwesomeIcon
                    icon={faPen}
                    style={{ color: "#6c757d", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditAddress(addr, i);
                    }}
                  />
                </div>
              ))
            )
              : (
                <p className="text-muted fs-13">No {defaultFor} address available</p>
              )}
          </>
        ) : (
          <Form onSubmit={handleSubmit(isEditing ? handleEditSave : handleFormSubmit)} className="form-container">
            <Form.Group className="mb-3 focus-z-index position-relative">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Address"
                {...register("address", { required: true })}
              />
              {errors.address && (
                <p
                  style={{ fontSize: "12px" }}
                  className="text-danger mt-1"
                >
                  {errors.address.message}
                </p>
              )}
            </Form.Group>
            <Form.Group
              className="mb-3 position-relative d-flex flex-column focus-z-index"
              controlId="formCountry"
            >
              <Form.Label
                style={{ marginBottom: "2px", marginTop: "2px" }}
              >
                Select Country
              </Form.Label>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={countries}
                    value={
                      field.value?.value
                        ? countries.find((c) => c.value === field.value.value) || {
                          value: field.value.value,
                          label: field.value.label,
                        }
                        : null
                    }
                    onChange={(selectedOption) => {
                      const newValue = selectedOption
                        ? { label: selectedOption.label, value: selectedOption.value }
                        : { label: "", value: "" };
                      field.onChange(newValue);
                      setValue("state", { label: "", value: "" });
                      setValue("city", { label: "", value: "" });
                    }}
                    isSearchable={true}
                    isClearable={true}
                    classNamePrefix="react-select"
                    styles={customStyles}
                    placeholder="Select Country"
                  />
                )}
              />
              {errors.country && (
                <p style={{ fontSize: "12px" }} className="text-danger mt-1">
                  {errors.country.message}
                </p>
              )}
            </Form.Group>
            <Form.Group
              className="mb-3 d-flex flex-column position-relative focus-z-index"
              controlId="formState"
            >
              <Form.Label
                style={{ marginBottom: "2px", marginTop: "2px" }}
              >
                Select State
              </Form.Label>
              <Controller
                name="state"
                control={control}
                rules={{ required: "State is Required" }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      styles={customStyles}
                      {...field}
                      options={states}
                      value={
                        states.find(
                          (s) => s.value === watch('state.value')
                        )
                          ? {
                            value: field.value,
                            label:
                              states.find(
                                (s) => s.value === watch('state.value')
                              )?.label || "",
                          }
                          : null
                      }
                      onChange={(selectedOption) => {
                        const newValue = selectedOption
                          ? { label: selectedOption.label, value: selectedOption.value }
                          : { label: "", value: "" };
                        field.onChange(newValue);
                        setValue("city", { label: "", value: "" });
                      }}
                      isSearchable
                      isClearable
                      placeholder="Select State"
                      classNamePrefix="react-select"
                      noOptionsMessage={() => "No states available"}
                    />
                  </>
                )}
              />
              {errors.state && (
                <p
                  style={{ fontSize: "12px" }}
                  className="text-danger mt-1"
                >
                  {errors.state.message}
                </p>
              )}
            </Form.Group>

            <Form.Group
              className="mb-3 position-relative d-flex flex-column focus-z-index"
              controlId="formCity"
            >
              <Form.Label
                style={{ marginBottom: "2px", marginTop: "2px" }}
              >
                Select City
              </Form.Label>
              <Controller
                name="city"
                control={control}
                rules={{ required: "City is Required" }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Select
                      styles={customStyles}
                      {...field}
                      options={cities}
                      value={
                        cities.find((c) => c.value === watch('city.value'))
                          ? {
                            value: field.value,
                            label:
                              cities.find(
                                (c) => c.value === watch('city.value')
                              )?.label || "",
                          }
                          : null
                      }
                      onChange={(selectedOption) => {
                        const newValue = selectedOption
                          ? { label: selectedOption.label, value: selectedOption.value }
                          : { label: "", value: "" };
                        field.onChange(newValue);
                      }}
                      isSearchable
                      isClearable
                      placeholder="Select City"
                      noOptionsMessage={() => "No cities available"}
                      classNamePrefix={"react-select"}
                    />
                    {error && (
                      <p
                        style={{ fontSize: "12px" }}
                        className="text-danger mt-1"
                      >
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </Form.Group>
            <Form.Group className="mb-3 focus-z-index position-relative">
              <Form.Label>Zipcode</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter Zipcode"
                {...register("zipcode", { required: true })}
              />

              {errors.zipcode && (
                <p
                  style={{ fontSize: "12px" }}
                  className="text-danger mt-1"
                >
                  {errors.zipcode.message}
                </p>
              )}
            </Form.Group>

            <Modal.Footer>
              <button
                className="btn btn-default-outline"
                variant="secondary"
                onClick={() => {
                  reset()
                  setShowAddForm(false);
                  setEditIndex(null);
                  setIsEditing(false);
                  reset({
                    address: "",
                    country: { label: "", value: "" },
                    state: { label: "", value: "" },
                    city: { label: "", value: "" },
                    zipcode: "",
                  });
                }}
              >
                Back
              </button>

              <button type="submit" variant={isEditing ? "warning" : "primary"} className="btn btn-default">
                {isEditing ? "Save Edited Address" : "Save Address"}
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Modal.Body>
      <style jsx>{`
        .custom-radio .form-check-input {
          width: 24px;
          height: 24px;
          background-color: var(--custom-white);
          border: 1px solid var(--input-border);
        }
        .custom-radio .form-check-input:checked {
          width: 24px;
          height: 24px;
          background-color: var(--primary-color);
          border-color: var(--primary-color);
        }
      `}</style>
    </Modal >

  );
};